import {API_BASE, apiGet, apiPatch, apiPost} from './client';
import {ACCOUNT_ID} from './account';
import {normalizeListItem, normalizeTale} from './taleAdapter';

// Voice catalog. Prefer the tales service (`GET /v1/voices` → {voices:[{id, name,
// caption, sample_url}]}); fall back to the legacy celebs catalog while the tales
// app isn't deployed to a given host (it still returns real sample clips). Both
// expose the same ids (friend/aunt/uncle).
export function getVoices(language = 'ru') {
    return apiGet(`/v1/voices?language=${encodeURIComponent(language)}`)
        .then(d => (Array.isArray(d) ? d : (d?.voices || [])))
        .then(list => (list.length ? list : Promise.reject(new Error('empty'))))
        .catch(() => apiGet('/celebs/skazki/voices'));
}

// Create a draft, set its params, and commit it for full generation — the
// /v1 tales replacement for the old one-shot `/celebs/skazki/generate`. Returns
// the normalized commit snapshot (status 'processing'); poll `getTale` until it
// becomes 'ready'. `interests` must be non-empty and `teaches` non-null or the
// commit is rejected (REQUIRED_PARAMS on the backend).
export async function startTale({
                                    accountId = ACCOUNT_ID,
                                    childName,
                                    age,
                                    interests,
                                    teaches,
                                    voiceId,
                                    language = 'ru',
                                }) {
    const idempotencyKey = crypto.randomUUID?.() || `${accountId}-${age}-${childName}`;
    const created = await apiPost('/v1/tales', {account_id: accountId, type: 'audio'},
        {'Idempotency-Key': idempotencyKey});
    const taleId = created?.tale?.id;
    if (!taleId) throw new Error('tale create failed');

    await apiPatch(`/v1/tales/${taleId}`, {
        account_id: accountId,
        child_name: childName,
        age,
        interests,
        teaches,
        voice_id: voiceId,
        language,
    });

    const committed = await apiPost(`/v1/tales/${taleId}/commit`, {account_id: accountId});
    return normalizeTale(committed);
}

export function getTale(taleId, {accountId = ACCOUNT_ID} = {}) {
    return apiGet(`/v1/tales/${taleId}?account_id=${encodeURIComponent(accountId)}`)
        .then(normalizeTale);
}

// Regenerate a preview tale in full (`POST /v1/tales/{id}/unlock`) — the backend
// debits one skazki credit and reruns generation from scratch. A 402
// (payment_required) bubbles up so the caller can open the purchase sheet.
export function unlockTale(taleId, {accountId = ACCOUNT_ID} = {}) {
    return apiPost(`/v1/tales/${taleId}/unlock`, {account_id: accountId})
        .then(normalizeTale);
}

// Word-level alignment for the read-along player. Served by the API
// (`GET /v1/tales/{id}/alignment`) because the S3 bucket has no CORS config,
// so browsers can't fetch alignment.json directly; the raw S3 URL stays as a
// fallback for a backend that doesn't have the endpoint yet.
export function getAlignment(taleId, {accountId = ACCOUNT_ID, fallbackUrl = null} = {}) {
    return apiGet(`/v1/tales/${taleId}/alignment?account_id=${encodeURIComponent(accountId)}`)
        .catch(err => {
            if (!fallbackUrl) throw err;
            return fetch(fallbackUrl).then(r => (r.ok ? r.json() : Promise.reject(err)));
        });
}

export function listTales(accountId = ACCOUNT_ID, {phase = 'committed', limit = 40} = {}) {
    const params = new URLSearchParams({account_id: accountId, limit: String(limit)});
    if (phase) params.set('phase', phase);
    return apiGet(`/v1/tales?${params.toString()}`)
        .then(d => ({tales: (d?.tales || []).map(normalizeListItem)}));
}

// Watch a committed tale's generation via the SSE event stream
// (`GET /v1/tales/{id}/events`) instead of polling. Each server event
// (asset.ready / tale.title / terminal) triggers a snapshot refetch so callers
// receive the same normalized shape as `getTale`. `onSnapshot(tale)` fires on
// progress + success; `onError('failed'|'canceled')` on a failure terminal or a
// permanently-dropped connection. Returns an unsubscribe function.
export function subscribeTale(taleId, {accountId = ACCOUNT_ID, onSnapshot, onError} = {}) {
    const url = `${API_BASE}/v1/tales/${encodeURIComponent(taleId)}`
        + `/events?account_id=${encodeURIComponent(accountId)}`;
    const es = new EventSource(url);
    let closed = false;
    const close = () => {
        if (closed) return;
        closed = true;
        try {
            es.close();
        } catch { /* ignore */
        }
    };
    // Refetch the full snapshot — a single event carries only a fragment, but the
    // assembled `playback` (audio + scenes) and `cover` live in the snapshot.
    const push = () => getTale(taleId, {accountId})
        .then(t => {
            if (!closed) onSnapshot?.(t);
        })
        .catch(() => { /* transient — keep listening */
        });

    ['asset.ready', 'asset.failed', 'asset.retrying', 'tale.title'].forEach(
        type => es.addEventListener(type, push));
    // Success terminals: one last refetch, then close the stream.
    ['tale.completed', 'tale.partial'].forEach(
        type => es.addEventListener(type, () => {
            push().finally(close);
        }));
    es.addEventListener('tale.failed', () => {
        close();
        onError?.('failed');
    });
    es.addEventListener('tale.canceled', () => {
        close();
        onError?.('canceled');
    });
    // Connection permanently failed (endpoint unreachable / stream errored):
    // do one final status check before giving up — the tale may already be done.
    es.addEventListener('error', () => {
        if (closed || es.readyState !== EventSource.CLOSED) return;
        close();
        getTale(taleId, {accountId})
            .then(t => (t.status === 'ready' && t.audio_url ? onSnapshot?.(t) : onError?.('failed')))
            .catch(() => onError?.('failed'));
    });

    return close;
}
